import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { supabase } from '../../lib/supabase';
import type { UserRole } from '../../types';
import styles from './WaitlistForm.module.css';

const MIN_FILL_MS = 2000;
const DUPLICATE_CODE = '23505';

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

interface WaitlistFormProps {
  /** Where the signup originated, stored alongside the row for attribution. */
  source?: string;
}

export default function WaitlistForm({ source = 'landing' }: WaitlistFormProps): JSX.Element {
  const [email, setEmail] = useState<string>('');
  const [role, setRole] = useState<UserRole>('creator');
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [honeypot, setHoneypot] = useState<string>('');
  const mountedAt = useRef<number | null>(null);

  useEffect(() => {
    mountedAt.current = Date.now();
  }, []);

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'submitting') return;

    // Bot heuristics: hidden field filled, or submitted inhumanly fast.
    // Pretend success so bots learn nothing.
    if (honeypot || mountedAt.current === null || Date.now() - mountedAt.current < MIN_FILL_MS) {
      setStatus('success');
      return;
    }

    if (!supabase) {
      setStatus('error');
      return;
    }

    setStatus('submitting');
    const { error } = await supabase.from('waitlist').insert({
      email: email.trim().toLowerCase(),
      role,
      source,
    });

    if (!error || error.code === DUPLICATE_CODE) {
      setStatus('success');
    } else {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className={styles.success} role="status">
        <div className={styles.successTitle}>You&rsquo;re on the list.</div>
        <p className={styles.successText}>
          We&rsquo;ll email you the moment the vault opens. No spam — that&rsquo;s the whole point.
        </p>
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={submit}>
      <div className={styles.roleRow}>
        <button
          type="button"
          className={`auth-role-btn${role === 'creator' ? ' active' : ''}`}
          onClick={() => setRole('creator')}
        >
          I&rsquo;m a Creator
        </button>
        <button
          type="button"
          className={`auth-role-btn${role === 'brand' ? ' active' : ''}`}
          onClick={() => setRole('brand')}
        >
          I&rsquo;m a Brand
        </button>
      </div>

      <div className={styles.inputRow}>
        <label className={styles.srOnly} htmlFor="waitlist-email">
          Email address
        </label>
        <input
          id="waitlist-email"
          className={`form-input ${styles.emailInput}`}
          type="email"
          required
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={status === 'submitting'}
        />
        <button type="submit" className="btn-gold" disabled={status === 'submitting'}>
          {status === 'submitting' ? 'Joining…' : 'Join the Waitlist'}
        </button>
      </div>

      {/* Honeypot: invisible to humans, irresistible to bots */}
      <input
        type="text"
        name="website"
        className={styles.honeypot}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
      />

      {status === 'error' && (
        <p className={styles.error} role="alert">
          Something went wrong joining the waitlist. Please try again in a moment.
        </p>
      )}
    </form>
  );
}
